import type { Column } from '@tanstack/react-table';
import { ChevronDownIcon, ChevronsUpDownIcon, ChevronUpIcon, XIcon } from 'lucide-react';
import React from 'react';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import { cn } from '@/shared/helpers';

type ColumnHeaderProps<TData, TValue> = Omit<
  React.ComponentProps<typeof DropdownMenuTrigger>,
  'asChild'
> & {
  column: Column<TData, TValue>;
};

export function ColumnHeader<TData, TValue>({
  column,
  title,
  className,
  children,
  ...props
}: ColumnHeaderProps<TData, TValue>) {
  const getIndicatorIcon = () => {
    if (!column.getCanSort()) {
      return null;
    }
    if (column.getIsSorted() === 'desc') {
      return <ChevronDownIcon />;
    }
    if (column.getIsSorted() === 'asc') {
      return <ChevronUpIcon />;
    }
    return <ChevronsUpDownIcon />;
  };

  return (
    <div
      className={cn(
        'flex w-full',
        {
          'justify-start': column.columnDef.meta?.align === 'left',
          'justify-center': column.columnDef.meta?.align === 'center',
          'justify-end': column.columnDef.meta?.align === 'right',
        },
        className,
      )}
    >
      {!column.getCanSort() ? (
        children
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={cn(
              // margin explanation: px-1.5 is countered by -mx-1.5 to ensure the left aligns with the column values
              // -my-1.5 counters the header cell padding, allowing the button to be h-8 while keeping the header cell the target ~40px
              // -has-[svg]:-mr-2 adds another .5 to counter for the inherent padding of the icon when it is present, specifically for right-aligned columns
              '-mx-1.5 -my-1.5 flex h-8 items-center gap-1.5 rounded-md px-1.5 py-1.5 hover:bg-accent focus:ring-1 focus:ring-ring focus:outline-none has-[svg]:-mr-2 data-[state=open]:bg-accent [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground',
              className,
            )}
            {...props}
          >
            {children}
            {getIndicatorIcon()}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-28">
            {column.getCanSort() && (
              <>
                <DropdownMenuCheckboxItem
                  className="relative pr-8 pl-2 [&_svg]:text-muted-foreground [&>span:first-child]:right-2 [&>span:first-child]:left-auto"
                  checked={column.getIsSorted() === 'asc'}
                  onClick={() => column.toggleSorting(false)}
                >
                  <ChevronUpIcon />
                  Asc
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  className="relative pr-8 pl-2 [&_svg]:text-muted-foreground [&>span:first-child]:right-2 [&>span:first-child]:left-auto"
                  checked={column.getIsSorted() === 'desc'}
                  onClick={() => column.toggleSorting(true)}
                >
                  <ChevronDownIcon />
                  Desc
                </DropdownMenuCheckboxItem>
                {column.getIsSorted() && (
                  <DropdownMenuItem
                    className="pl-2 [&_svg]:text-muted-foreground"
                    onClick={() => column.clearSorting()}
                  >
                    <XIcon />
                    Reset
                  </DropdownMenuItem>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
